import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { useUserWithAccount } from "context/UserAccountProvider";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  IDraftRunSummary,
  useCreateContractModelDraftRunMutation,
  useListContractModelDraftRunsQuery,
} from "store/queries/contractModelDrafts";

import {
  formatRunDate,
  runDisplayName,
  runStatusLabel,
  runStatusSeverity,
} from "./utils";

// Temporary: restrict this page to a single user while it's in early access.
const ALLOWED_EMAIL = "mike.over@clearhealthinc.com";

// Module-level table cell renderers (not defined during render).
const nameBody = (r: IDraftRunSummary) => (
  <span className="font-medium">{runDisplayName(r)}</span>
);
const statusBody = (r: IDraftRunSummary) => (
  <Tag
    value={runStatusLabel(r.status)}
    severity={runStatusSeverity(r.status)}
  />
);
const createdBody = (r: IDraftRunSummary) => formatRunDate(r.createdAt);

export function ContractModelDrafts() {
  const { userInfo } = useUserWithAccount();
  const isAllowed = (userInfo?.email ?? "").toLowerCase() === ALLOWED_EMAIL;
  const navigate = useNavigate();

  const { data: runs = [] } = useListContractModelDraftRunsQuery(undefined, {
    skip: !isAllowed,
  });
  const [createRun, { isLoading: creating }] =
    useCreateContractModelDraftRunMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");

  const handleCreate = async () => {
    const created = await createRun({
      name: name.trim() || undefined,
    }).unwrap();
    setShowCreate(false);
    setName("");
    navigate(`/contract-model-drafts/${created.id}`);
  };

  if (!isAllowed) return <Navigate to="/" replace />;

  const openRun = (id: string) => navigate(`/contract-model-drafts/${id}`);

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <h1 className="m-0">AI Contract Modeling</h1>
        <Button
          label="New run"
          icon="pi pi-plus"
          onClick={() => setShowCreate(true)}
        />
      </div>

      <PageDataTable
        value={[...runs]}
        dataKey="id"
        emptyMessage="No runs yet. Click “New run” to model a contract."
        rowClassName={() => "cursor-pointer"}
        onRowClick={(e) => openRun((e.data as IDraftRunSummary).id)}
      >
        <Column header="Name" body={nameBody} />
        <Column header="Status" body={statusBody} />
        <Column header="Documents" field="documentCount" />
        <Column header="Recommendations" field="recommendationCount" />
        <Column header="Created" body={createdBody} />
      </PageDataTable>

      <Dialog
        header="New contract modeling run"
        visible={showCreate}
        style={{ width: "30rem" }}
        onHide={() => setShowCreate(false)}
        footer={
          <>
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={() => setShowCreate(false)}
            />
            <Button
              label="Create"
              icon="pi pi-check"
              disabled={creating}
              onClick={handleCreate}
            />
          </>
        }
      >
        <label className="block mb-2 font-medium" htmlFor="run-name">
          Name
        </label>
        <InputText
          id="run-name"
          className="w-full"
          value={name}
          placeholder="e.g. Heritage Aetna"
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
        />
        <small className="text-500 block mt-2">
          You&rsquo;ll upload documents and generate the model on the next
          screen.
        </small>
      </Dialog>
    </div>
  );
}
